import { env } from "@/config/env"
import { HttpClient } from "@/shared/lib/http-client"

const RECEIVED_PDF_REPORT_KEY = "RECEIVED_PDF_REPORT"

/**
 * Birth-data snapshot sent with a public report request. Every field is optional:
 * each report reads only the subset it needs, so the per-offer builders in
 * `../lib/build-report-user` populate just those and omit the rest (see there for
 * which report consumes what).
 */
export interface PdfReportUserPayload {
  dayOfBirth?: number
  monthOfBirth?: number
  yearOfBirth?: number
  hourOfBirth?: number
  minuteOfBirth?: number
  gender?: string
  maritalStatus?: string | null
  name?: string
  zodiac?: string
  timeFormat?: string
  isPassedQuiz?: boolean
}

function alreadyRequested(key: string): boolean {
  try {
    return window.sessionStorage.getItem(RECEIVED_PDF_REPORT_KEY) === key
  } catch {
    return false
  }
}

function markRequested(key: string): void {
  try {
    window.sessionStorage.setItem(RECEIVED_PDF_REPORT_KEY, key)
  } catch {
    /* ignore */
  }
}

class PdfReportService extends HttpClient {
  /** Authed (webapp): backend derives email/profile from the Firebase token. */
  async sendForMe(params: { subscriptionId: string; token: string }): Promise<void> {
    const key = `me:${params.subscriptionId}`
    if (alreadyRequested(key)) return

    await this.post(
      "/v1/reports/me/star",
      { subscriptionId: params.subscriptionId },
      { headers: { Authorization: `Bearer ${params.token}` } },
    )

    markRequested(key)
  }

  /**
   * Authed (webapp): request the static Love Match report for the signed-in user.
   * The backend writes a `completed` record (static url) immediately.
   */
  async sendLoveMatchForMe(params: { subscriptionId: string; token: string }): Promise<void> {
    const key = `me:love-match:${params.subscriptionId}`
    if (alreadyRequested(key)) return

    await this.post(
      "/v1/reports/me/love-match",
      { subscriptionId: params.subscriptionId },
      { headers: { Authorization: `Bearer ${params.token}` } },
    )

    markRequested(key)
  }

  /** Public (quiz/additional-offers): pre-account star report, birth snapshot. */
  async sendPublic(params: {
    user: PdfReportUserPayload
    email: string
    subscriptionId: string
  }): Promise<void> {
    const email = params.email.toLowerCase()
    if (alreadyRequested(`star:${email}`)) return

    await this.post("/v1/reports/star", {
      subscriptionId: params.subscriptionId,
      email,
      user: params.user,
    })

    markRequested(`star:${email}`)
  }

  /** Public (additional-offers): pre-account Love Match report, birth snapshot. */
  async sendPublicLoveMatch(params: {
    user: PdfReportUserPayload
    email: string
    subscriptionId: string
  }): Promise<void> {
    const email = params.email.toLowerCase()
    if (alreadyRequested(`love-match:${email}`)) return

    await this.post("/v1/reports/love-match", {
      subscriptionId: params.subscriptionId,
      email,
      user: params.user,
    })

    markRequested(`love-match:${email}`)
  }

  /**
   * Authed (webapp): request the static Astro Map report for the signed-in user.
   * The backend picks the PDF by the profile's zodiac sign, records it and emails.
   */
  async sendAstroMapForMe(params: { subscriptionId: string; token: string }): Promise<void> {
    const key = `me:astro-map:${params.subscriptionId}`
    if (alreadyRequested(key)) return

    await this.post(
      "/v1/reports/me/astro-map",
      { subscriptionId: params.subscriptionId },
      { headers: { Authorization: `Bearer ${params.token}` } },
    )

    markRequested(key)
  }

  /** Public (additional-offers): pre-account Astro Map report, birth snapshot. */
  async sendPublicAstroMap(params: {
    user: PdfReportUserPayload
    email: string
    subscriptionId: string
  }): Promise<void> {
    const email = params.email.toLowerCase()
    if (alreadyRequested(`astro-map:${email}`)) return

    await this.post("/v1/reports/astro-map", {
      subscriptionId: params.subscriptionId,
      email,
      user: params.user,
    })

    markRequested(`astro-map:${email}`)
  }
}

const pdfReportService = new PdfReportService({ baseURL: env.VITE_API_URL })

/**
 * Webapp: requests the star report for the signed-in user via
 * `POST /v1/me/reports/star` (only the subscription id; the backend reads the
 * profile and verifies the subscription from the token).
 */
export async function requestMyStarReport(params: {
  subscriptionId: string
  token: string
}): Promise<void> {
  if (!env.VITE_API_URL) {
    return
  }

  await pdfReportService.sendForMe(params)
}

/**
 * Webapp: requests the static Love Match report for the signed-in user via
 * `POST /v1/reports/me/love-match` (only the subscription id; the backend reads
 * the profile and verifies the subscription from the token). Best-effort.
 */
export async function requestMyLoveMatchReport(params: {
  subscriptionId: string
  token: string
}): Promise<void> {
  if (!env.VITE_API_URL) {
    return
  }

  await pdfReportService.sendLoveMatchForMe(params)
}

/**
 * Quiz / additional-offers (pre-account): requests the star report via the
 * public `POST /v1/reports/star` with a birth snapshot (see
 * {@link buildStarReportUser}).
 */
export async function requestPdfReport(params: {
  user: PdfReportUserPayload
  email: string
  subscriptionId: string
}): Promise<void> {
  if (!env.VITE_API_URL) {
    return
  }

  await pdfReportService.sendPublic(params)
}

/**
 * Additional-offers (pre-account): requests the Love Match report via the public
 * `POST /v1/reports/love-match` with a birth snapshot. Mirrors
 * {@link requestPdfReport}; the backend records it and emails the static PDF.
 */
export async function requestLoveMatchReport(params: {
  user: PdfReportUserPayload
  email: string
  subscriptionId: string
}): Promise<void> {
  if (!env.VITE_API_URL) {
    return
  }

  await pdfReportService.sendPublicLoveMatch(params)
}

/**
 * Webapp: requests the static Astro Map report for the signed-in user via
 * `POST /v1/reports/me/astro-map` (only the subscription id; the backend reads
 * the profile, picks the PDF by zodiac and verifies the subscription). Best-effort.
 */
export async function requestMyAstroMapReport(params: {
  subscriptionId: string
  token: string
}): Promise<void> {
  if (!env.VITE_API_URL) {
    return
  }

  await pdfReportService.sendAstroMapForMe(params)
}

/**
 * Additional-offers (pre-account): requests the Astro Map report via the public
 * `POST /v1/reports/astro-map` with a birth snapshot. Mirrors
 * {@link requestLoveMatchReport}; the backend records it and emails the PDF for
 * the snapshot's zodiac sign (see {@link buildAstroMapReportUser}).
 */
export async function requestAstroMapReport(params: {
  user: PdfReportUserPayload
  email: string
  subscriptionId: string
}): Promise<void> {
  if (!env.VITE_API_URL) {
    return
  }

  await pdfReportService.sendPublicAstroMap(params)
}
